import { FC, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import parse from 'html-react-parser';

import { COMMON_CONTAINER_ID } from '@/config';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { profileSelectors } from '@/redux/selectors';
import { blockActions, profileActions } from '@/redux/actions';
import { useInputText } from '@/hooks';
import { Validation } from '@/models';
import { SetBalanceAlertRequest } from '@/api';
import { AlertBox, Button, Input, InputGroup, InputSelect } from '@/components';
import { ModalInfo, ModalInfoProps } from '../ModalInfo';

import styles from './CriticalBalanceAlert.module.sass';

const criticalOptionLabel = (label: string): string => {
  return `critical_${label.replace(/[^a-zA-Z0-9-]/, '').replace(' ', '_')}`;
};

type AlertOption = {
  value: string;
  label: string;
};

interface CriticalBalanceAlertProps extends ModalInfoProps {}

const CriticalBalanceAlert: FC<CriticalBalanceAlertProps> = (props) => {
  const { isOpen, onClose } = props;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const {
    data: profileData,
    loading,
    error,
  } = useAppSelector(profileSelectors.getProfileSlice);

  const periodOptions: AlertOption[] = !profileData?.balanceAlertsPeriods
    ? []
    : Object.entries(profileData.balanceAlertsPeriods).map(
        ([value, _label]) => {
          return {
            value,
            label: t(`translation.${criticalOptionLabel(value)}`),
          };
        },
      );

  const LEVEL_VALIDATIONS: Validation[] = useMemo(
    () => [
      {
        name: 'required',
        value: true,
        message: t('translation.field_is_required'),
      },
      {
        name: 'regex',
        value: /^\d+$/i,
        message: t('translation.field_must_contain_only_digits'),
      },
      {
        name: 'gt',
        value: 0,
        message: t('translation.field_must_be_greater', { value: 0 }),
      },
    ],
    [t],
  );

  const PERIOD_VALIDATIONS: Validation[] = useMemo(
    () => [
      {
        name: 'required',
        value: true,
        message: t('translation.field_is_required'),
      },
    ],
    [t],
  );

  const levelField = useInputText(
    profileData?.balanceAlertsSets?.level || '',
    LEVEL_VALIDATIONS,
  );

  const periodField = useInputText(
    profileData?.balanceAlertsSets?.period || periodOptions[0]?.value || '',
    PERIOD_VALIDATIONS,
  );

  useEffect(() => {
    if (isOpen) {
      levelField.onReset();
      levelField.onChange(profileData?.balanceAlertsSets?.level || '');
      periodField.onChange(
        profileData?.balanceAlertsSets?.period || periodOptions[0]?.value || '',
      );
      dispatch(blockActions.block());
    } else {
      dispatch(blockActions.unblock());
    }
    dispatch(profileActions.setError(null));
  }, [isOpen]);

  const submitHandler = () => {
    if (!loading) {
      if (levelField.isValid && periodField.isValid) {
        const payload: SetBalanceAlertRequest = {
          level: levelField.value,
          period: periodField.value,
        };

        dispatch(profileActions.updateAlertPeriod(payload));
      } else {
        if (!levelField.isValid) {
          levelField.onBlur();
        }

        if (!periodField.isValid) {
          periodField.onBlur();
        }
      }
    }
  };

  return (
    <ModalInfo
      isOpen={isOpen}
      title={t('translation.bal_alerts_title_str')}
      actions={
        <>
          <Button
            type="primary"
            corner="rect"
            disabled={loading}
            isLoading={loading}
            onClick={submitHandler}
          >
            {t('translation.save_changes_btn')}
          </Button>
          <Button
            type="secondary"
            corner="rect"
            onClick={onClose}
            disabled={loading}
          >
            {t('translation.cancel_btn')}
          </Button>
        </>
      }
      onClose={onClose}
    >
      <div className={styles.form}>
        <InputGroup
          label={t('translation.bal_alert_level_str')}
          className={styles.group}
        >
          <Input
            id="level"
            name="level"
            type="text"
            className={styles.input}
            value={levelField.value}
            onChange={levelField.onChange}
            onBlur={levelField.onBlur}
            isError={levelField.isDirty && !levelField.isValid}
          />
          {levelField.isDirty && !levelField.isValid && (
            <AlertBox type="danger" small>
              {{ description: levelField.errors[0] }}
            </AlertBox>
          )}
        </InputGroup>

        <InputGroup
          label={t('translation.bal_alert_period_str')}
          className={styles.group}
        >
          <InputSelect
            id="period"
            name="period"
            classNamePrefix="chain-select"
            value={periodField.value}
            options={periodOptions}
            onChange={periodField.onChange}
            onBlur={periodField.onBlur}
          />
        </InputGroup>
      </div>

      {!!error && (
        <AlertBox type="danger" className={styles.alert}>
          {{
            title: parse(t('translation.error')),
            description: error,
            icon: '🤔',
          }}
        </AlertBox>
      )}
    </ModalInfo>
  );
};

export default CriticalBalanceAlert;
